/**
 * TaskSetProfileProposalDiff
 *
 * Builds the RPilot preview tree for a proposed task set profile commit
 * (rdrive_propose_task_set_profile_commit): the proposed taskProfiles tree
 * annotated with change types against the existing profile, plus the
 * display-ready subtask rows for SubtasksDialog.
 *
 * Identity is the global versionReference, matching detectChanges() in
 * TaskSetProfileChangeDetector: a task moved to a different parent is one
 * (possibly modified) task at its new position, not a delete-plus-recreate.
 * Unlike the Excel review flow, removed tasks are rendered too — as red rows
 * grafted under their original parent (or at the root when the parent itself
 * is gone), so the user sees deletions before approving.
 */

import {
  CHANGE_TYPES,
  normalizeValue,
  arraysEqual,
  flattenTasks
} from './TaskSetProfileChangeDetector';
import RandomIdGenerator from './RandomIdGenerator';

/**
 * Build the preview for a proposed profile against the existing one.
 *
 * @param {Array} proposedTaskProfiles - taskProfiles tree from the proposal
 * @param {Array} existingTaskProfiles - taskProfiles tree of the current profile ([] on create)
 * @param {Object} [lookups] - name resolvers for subtask rows
 * @param {Map<string,string>} [lookups.teamMap] - teamId → team name
 * @param {Map<string,string>} [lookups.templateMap] - itemTemplateCode → template title
 * @returns {{ tree: Array, subtaskMap: Map<string, Array>, counts: Object }}
 *   tree: Bryntum-ready rows { id, name, code, changeType, changeDetails,
 *         changeCount, subtaskCount, subtaskChanges, expanded, children? }
 *   subtaskMap: row id → SubtasksDialog rows
 *   counts: { new, updated, deleted, subtasksNew, subtasksUpdated, subtasksDeleted }
 */
export function buildProposalPreview(proposedTaskProfiles, existingTaskProfiles, lookups = {}) {
  const teamMap = lookups.teamMap || new Map();
  const templateMap = lookups.templateMap || new Map();

  const existingByVr = new Map(
    flattenTasks(existingTaskProfiles || [])
      .filter((t) => t.versionReference)
      .map((t) => [t.versionReference, t])
  );

  const seenVrs = new Set();
  const rowByVr = new Map();
  const subtaskMap = new Map();
  const counts = {
    new: 0, updated: 0, deleted: 0,
    subtasksNew: 0, subtasksUpdated: 0, subtasksDeleted: 0
  };
  let newRowSeq = 0;

  const resolveTeam = (id) => teamMap.get(id) || null;
  const resolveTemplate = (code) => templateMap.get(code) || null;

  function buildSubtaskRows(proposedSubtasks, existingSubtasks) {
    const existingByRef = new Map(
      (existingSubtasks || [])
        .filter((s) => s.versionReference)
        .map((s) => [s.versionReference, s])
    );
    const seenRefs = new Set();

    const rows = (proposedSubtasks || []).map((subtask) => {
      const existing = subtask.versionReference
        ? existingByRef.get(subtask.versionReference)
        : undefined;
      let changeType = CHANGE_TYPES.UNCHANGED;
      const changeDetails = [];

      if (!existing) {
        changeType = CHANGE_TYPES.ADDED;
        changeDetails.push('New subtask added');
      } else {
        seenRefs.add(subtask.versionReference);
        if (normalizeValue(subtask.title) !== normalizeValue(existing.title)) {
          changeDetails.push(`Title: "${existing.title || ''}" → "${subtask.title || ''}"`);
        }
        if (normalizeValue(subtask.defaultLeadByTeamId) !== normalizeValue(existing.defaultLeadByTeamId)) {
          const oldTeam = resolveTeam(existing.defaultLeadByTeamId) || existing.defaultLeadByTeamId || '(none)';
          const newTeam = resolveTeam(subtask.defaultLeadByTeamId) || subtask.defaultLeadByTeamId || '(none)';
          changeDetails.push(`Lead team: ${oldTeam} → ${newTeam}`);
        }
        if (normalizeValue(subtask.weighting) !== normalizeValue(existing.weighting)) {
          changeDetails.push(`Weighting: ${existing.weighting ?? '(none)'} → ${subtask.weighting ?? '(none)'}`);
        }
        if (normalizeValue(subtask.itemTemplateCode) !== normalizeValue(existing.itemTemplateCode)) {
          changeDetails.push(`Template: ${existing.itemTemplateCode || '(none)'} → ${subtask.itemTemplateCode || '(none)'}`);
        }
        if (changeDetails.length > 0) changeType = CHANGE_TYPES.MODIFIED;
      }

      return {
        title: subtask.title,
        versionReference: subtask.versionReference,
        weighting: subtask.weighting,
        itemTemplateCode: subtask.itemTemplateCode,
        itemTemplateTitle: resolveTemplate(subtask.itemTemplateCode),
        defaultLeadByTeamId: subtask.defaultLeadByTeamId,
        leadTeamName: resolveTeam(subtask.defaultLeadByTeamId),
        changeType,
        changeDetails
      };
    });

    for (const existing of existingSubtasks || []) {
      if (existing.versionReference && seenRefs.has(existing.versionReference)) continue;
      rows.push({
        title: existing.title,
        versionReference: existing.versionReference,
        weighting: existing.weighting,
        itemTemplateCode: existing.itemTemplateCode,
        itemTemplateTitle: resolveTemplate(existing.itemTemplateCode),
        defaultLeadByTeamId: existing.defaultLeadByTeamId,
        leadTeamName: resolveTeam(existing.defaultLeadByTeamId),
        changeType: CHANGE_TYPES.REMOVED,
        changeDetails: ['Subtask removed']
      });
    }

    return rows;
  }

  function attachSubtasks(row, subtaskRows) {
    subtaskMap.set(row.id, subtaskRows);
    row.subtaskCount = subtaskRows.length;
    row.subtaskChanges = subtaskRows.filter((s) => s.changeType !== CHANGE_TYPES.UNCHANGED).length;
    counts.subtasksNew += subtaskRows.filter((s) => s.changeType === CHANGE_TYPES.ADDED).length;
    counts.subtasksUpdated += subtaskRows.filter((s) => s.changeType === CHANGE_TYPES.MODIFIED).length;
    counts.subtasksDeleted += subtaskRows.filter((s) => s.changeType === CHANGE_TYPES.REMOVED).length;
  }

  function buildProposedRow(task) {
    const existing = task.versionReference ? existingByVr.get(task.versionReference) : undefined;
    let changeType = CHANGE_TYPES.UNCHANGED;
    const changeDetails = [];
    let rowId;

    if (!existing) {
      changeType = CHANGE_TYPES.ADDED;
      changeDetails.push('New task added');
      counts.new += 1;
      rowId = `new-${newRowSeq++}`;
    } else {
      seenVrs.add(task.versionReference);
      rowId = `vr-${task.versionReference}`;
      if (normalizeValue(task.title) !== normalizeValue(existing.title)) {
        changeDetails.push(`Title: "${existing.title || ''}" → "${task.title || ''}"`);
      }
      if (normalizeValue(task.code) !== normalizeValue(existing.code)) {
        changeDetails.push(`Code: "${existing.code || '(empty)'}" → "${task.code || '(empty)'}"`);
      }
      if (!arraysEqual(task.taskCodes || [], existing.taskCodes || [])) {
        changeDetails.push('Task codes changed');
      }
      if (changeDetails.length > 0) {
        changeType = CHANGE_TYPES.MODIFIED;
        counts.updated += 1;
      }
    }

    const row = {
      id: rowId,
      name: task.title,
      code: task.code || '',
      changeType,
      changeDetails,
      changeCount: changeDetails.length,
      children: (task.children || []).map(buildProposedRow)
    };
    if (existing) rowByVr.set(task.versionReference, row);
    attachSubtasks(row, buildSubtaskRows(task.subtaskProfiles, existing?.subtaskProfiles));
    return row;
  }

  // A removed task renders with its removed descendants. Descendants that
  // were kept (moved elsewhere in the proposal) already have a row at their
  // new position — recurse through them so *their* removed children still
  // surface, attached to the moved task's row.
  function buildRemovedRow(existingTask) {
    const row = {
      id: `rem-${existingTask.versionReference || `noref-${newRowSeq++}`}`,
      name: existingTask.title,
      code: existingTask.code || '',
      changeType: CHANGE_TYPES.REMOVED,
      changeDetails: ['Task removed'],
      changeCount: 1,
      children: []
    };
    counts.deleted += 1;
    attachSubtasks(row, buildSubtaskRows([], existingTask.subtaskProfiles));
    collectRemoved(existingTask.children || [], row.children);
    return row;
  }

  function collectRemoved(existingList, fallbackTarget) {
    for (const existingTask of existingList) {
      if (existingTask.versionReference && seenVrs.has(existingTask.versionReference)) {
        const keptRow = rowByVr.get(existingTask.versionReference);
        collectRemoved(existingTask.children || [], keptRow ? keptRow.children : fallbackTarget);
      } else {
        fallbackTarget.push(buildRemovedRow(existingTask));
      }
    }
  }

  const tree = (proposedTaskProfiles || []).map(buildProposedRow);
  collectRemoved(existingTaskProfiles || [], tree);

  // Expand every row that has a change anywhere beneath it; drop empty
  // children arrays so Bryntum treats those rows as leaves.
  function finalize(row) {
    let expanded = row.changeType !== CHANGE_TYPES.UNCHANGED || row.subtaskChanges > 0;
    for (const child of row.children) {
      if (finalize(child)) expanded = true;
    }
    if (row.children.length === 0) delete row.children;
    row.expanded = expanded;
    return expanded;
  }
  tree.forEach(finalize);

  return { tree, subtaskMap, counts };
}

/**
 * Light structural validation of a proposed taskProfiles tree, run before
 * showing the preview so the agent gets immediate feedback instead of an API
 * rejection after the user approves. Returns error strings; empty when ok.
 */
export function validateProposedTaskProfiles(taskProfiles) {
  const errors = [];
  if (!Array.isArray(taskProfiles)) {
    return ['taskProfiles must be an array of task profile objects'];
  }
  if (taskProfiles.length === 0) {
    errors.push('taskProfiles is empty — a profile needs at least one task');
  }

  // Agents sometimes serialise a list field as a JSON string ("[]") or an
  // object instead of an array. Report that as a validation error so the
  // agent can correct it, rather than letting a downstream forEach/map throw.
  const listField = (task, field, where) => {
    const value = task[field];
    if (value == null) return [];
    if (Array.isArray(value)) return value;
    errors.push(
      `Task "${task.title || where}" has ${field} of type ${typeof value} — it must be a JSON array (use [] when empty)`
    );
    return [];
  };

  function walk(tasks, path) {
    tasks.forEach((task, index) => {
      const where = path ? `${path} > ${index + 1}` : `${index + 1}`;
      if (!task || typeof task !== 'object' || Array.isArray(task)) {
        errors.push(`Task at position ${where} is not an object`);
        return;
      }
      if (!task.title) {
        errors.push(`Task at position ${where} is missing a title`);
      }
      listField(task, 'taskCodes', where);
      for (const subtask of listField(task, 'subtaskProfiles', where)) {
        if (!subtask || typeof subtask !== 'object' || Array.isArray(subtask)) {
          errors.push(`A subtask of "${task.title || where}" is not an object`);
          continue;
        }
        if (!subtask.title) {
          errors.push(`A subtask of "${task.title || where}" is missing a title`);
        }
        if (subtask.itemType && !['process', 'issue'].includes(subtask.itemType)) {
          errors.push(
            `Subtask "${subtask.title || '(untitled)'}" of "${task.title || where}" has invalid itemType "${subtask.itemType}" — use "process" or "issue"`
          );
        }
      }
      walk(listField(task, 'children', where), where);
    });
  }
  walk(taskProfiles, '');

  return errors;
}

/**
 * Hydrate a proposed taskProfiles tree with versionReferences before saving.
 *
 * The API requires every task profile and subtask profile to carry a
 * versionReference — non-empty, and unique within the profile per category —
 * but never generates them: the reference web app mints them client-side at
 * creation time (8 chars, A–Z0–9). It is also the identity that correlates
 * rows across profile revisions, so existing rows must keep theirs:
 *  - a row that echoes a versionReference keeps it; a duplicate within the
 *    payload keeps the first occurrence's identity and later occurrences
 *    become new rows with fresh references
 *  - a row without one is re-attached to an existing row when possible (task
 *    profiles by code, then title, among the matched parent's children;
 *    subtask profiles by title + itemTemplateCode within the matched task) —
 *    unless that existing row's reference appears explicitly elsewhere in the
 *    payload — so an agent transform that drops references doesn't read as
 *    delete-plus-recreate
 *  - anything still without one gets a fresh reference that collides with
 *    nothing in the payload or the existing profile
 *
 * Returns a new tree; the input is not mutated. Malformed (non-object)
 * entries pass through for validateProposedTaskProfiles to report.
 *
 * @param {Array} proposedTaskProfiles - taskProfiles tree from the proposal
 * @param {Array} existingTaskProfiles - taskProfiles tree of the current profile ([] on create)
 * @param {Function} [generateId] - id factory override (tests)
 * @returns {Array} hydrated taskProfiles tree
 */
export function hydrateProposedTaskProfiles(proposedTaskProfiles, existingTaskProfiles = [], generateId = null) {
  const idGenerator = new RandomIdGenerator();
  const generate = generateId || (() => idGenerator.generate());
  const trimmedRef = (row) =>
    typeof row?.versionReference === 'string' ? row.versionReference.trim() : '';

  const knownRefs = new Set();
  const existingByVr = new Map();
  flattenTasks(existingTaskProfiles || []).forEach((task) => {
    if (task.versionReference) {
      existingByVr.set(task.versionReference, task);
      knownRefs.add(task.versionReference);
    }
    (task.subtaskProfiles || []).forEach((subtask) => {
      if (subtask.versionReference) knownRefs.add(subtask.versionReference);
    });
  });

  // References the payload supplies explicitly, anywhere — those existing
  // rows are spoken for and must not be re-attached to a reference-less row.
  const explicitRefs = new Set();
  (function collect(tasks) {
    (tasks || []).forEach((task) => {
      if (!task || typeof task !== 'object') return;
      const ref = trimmedRef(task);
      if (ref) explicitRefs.add(ref);
      (task.subtaskProfiles || []).forEach((subtask) => {
        const subtaskRef = trimmedRef(subtask);
        if (subtaskRef) explicitRefs.add(subtaskRef);
      });
      collect(task.children);
    });
  })(proposedTaskProfiles);

  const usedTaskRefs = new Set();
  const usedSubtaskRefs = new Set();
  const freshRef = () => {
    let ref;
    do { ref = generate(); } while (knownRefs.has(ref) || usedTaskRefs.has(ref) || usedSubtaskRefs.has(ref));
    return ref;
  };
  const claimable = (row) =>
    row.versionReference &&
    !explicitRefs.has(row.versionReference) &&
    !usedTaskRefs.has(row.versionReference) &&
    !usedSubtaskRefs.has(row.versionReference);

  const hydrateSubtasks = (subtasks, existingSubtasks) =>
    (subtasks || []).map((subtask) => {
      if (!subtask || typeof subtask !== 'object' || Array.isArray(subtask)) return subtask;
      let ref = trimmedRef(subtask);
      if (usedSubtaskRefs.has(ref)) ref = '';
      if (!ref) {
        const match = (existingSubtasks || []).find((existing) =>
          claimable(existing) &&
          existing.title === subtask.title &&
          (existing.itemTemplateCode || '') === (subtask.itemTemplateCode || ''));
        ref = match?.versionReference || freshRef();
      }
      usedSubtaskRefs.add(ref);
      return { ...subtask, versionReference: ref };
    });

  const hydrateTasks = (tasks, existingSiblings) =>
    (tasks || []).map((task) => {
      if (!task || typeof task !== 'object' || Array.isArray(task)) return task;
      let ref = trimmedRef(task);
      if (usedTaskRefs.has(ref)) ref = '';
      let existing = ref ? (existingByVr.get(ref) || null) : null;
      if (!ref) {
        const siblings = (existingSiblings || []).filter(claimable);
        existing =
          siblings.find((sibling) => sibling.code && sibling.code === task.code) ||
          siblings.find((sibling) => sibling.title === task.title) ||
          null;
        ref = existing?.versionReference || freshRef();
      }
      usedTaskRefs.add(ref);
      return {
        ...task,
        versionReference: ref,
        subtaskProfiles: hydrateSubtasks(task.subtaskProfiles, existing?.subtaskProfiles),
        children: hydrateTasks(task.children, existing?.children)
      };
    });

  return hydrateTasks(proposedTaskProfiles, existingTaskProfiles || []);
}
